/**
 * Comprehensive Platform-Specific Analytics Plugin
 *
 * This plugin provides functionality for tracking events across multiple platforms:
 * - Google Analytics 4 (GA4) for all platforms
 * - Adjust and Firebase for non-web platforms only
 *
 * It uses an environment variable to determine the platform type and
 * conditionally imports and uses platform-specific SDKs.
 */

import axios from "axios";
import { cookie, hashString, formatMobileNumber, generateEventId } from "../index";
import auth from "../auth";
import {
	registrationCategories,
	loginCategories,
	depositCategories,
	withdrawCategories,
	casinoCategories,
	generalCategories,
	betSlipCategories,
	jackpotsBetCategories,
	shikishaBetCategories,
	virtualsBetCategories,
	sportsbookBetCategories,
	iframeCategories,
	betikaFastaCasinoCategories,
	experimentCategories,
} from "./config";
import config from "../../config/config";

// Global variables
let LANGUAGE_CODE = window.navigator.language;
let COUNTRY_CODE = process.env.COUNTRY_CODE;
let GEOLOCATION = Intl.DateTimeFormat().resolvedOptions().timeZone;

const env = config[process.env.COUNTRY_CODE]
	? config[process.env.COUNTRY_CODE]
	: config;

// Event source constants
const miniappSources = env.miniapp.utmSrc;
const MINIAPP_UTM_SRC = "mpesa-mini-app";
const DEFAULT_SRC = "mobile-web";

// Cached event source - evaluated once per session for performance
let cachedEventSource = null;

window.dataLayer = window.dataLayer || [];

/**
 * this helper function determines the source (src) attribute for analytics events.
 * Returns "mpesa-mini-app" if user came from mini-app, otherwise "mobile-web".
 * Uses caching to avoid repeated cookie reads on every event.
 * @returns {string} - The source identifier
 */
function getEventSource() {
	// Return cached value if already determined
	if (cachedEventSource !== null) {
		return cachedEventSource;
	}

	try {
		const utmSource = cookie.get("utm_source");
		cachedEventSource = (utmSource && miniappSources.includes(utmSource))
			? utmSource
			: DEFAULT_SRC;
	} catch (e) {
		// Fallback to default if cookie read fails
		console.warn("[ANALYTICS] Error reading utm_source cookie:", e);
		cachedEventSource = DEFAULT_SRC;
	}

	return cachedEventSource;
}

/**
 * Fetches user information from the server and updates global variables.
 * @returns {Promise<void>}
 */
async function getUserInfo() {
	try {
		const userInfo = (await axios.get(window.USERINFO_URL)).data;
		if (userInfo.language) LANGUAGE_CODE = userInfo.language;
		if (userInfo.country) COUNTRY_CODE = userInfo.country;
		if (userInfo.geo) GEOLOCATION = userInfo.geo;
		updateCookies();
	} catch (e) {
		console.warn("[ANALYTICS] Unable to get user info", e);
	}
}

/**
 * Updates cookies with user and session information.
 */
function updateCookies() {
	const token = localStorage.token ? JSON.parse(localStorage.token) : null;
	if (!token) return;

	const gaCookie = cookie.get("_ga");
	const ga4SessionCookie = window.COOKIE_ID ? cookie.get(window.COOKIE_ID) : null;
	// Cookies may be absent (ad-blocker, GDPR opt-out, brand-new session) — skip silently
	if (!gaCookie || !ga4SessionCookie) return;

	const cid = gaCookie.split(".").slice(2).join(".");
	const ga4cid = cid;
	const ga4sid = ga4SessionCookie.split(".")[2];

	const savedCid = localStorage.cid;
	const savedGa4Cid = localStorage.ga4cid;
	const savedSid = localStorage.ga4sid;

	if (savedSid === ga4sid && savedCid === cid && savedGa4Cid === ga4cid) {
		return;
	}

	axios({
		method: "post",
		url: window.USERINFO_URL,
		headers: {
			"x-ga-cid": cid,
			"x-ga4-cid": ga4cid,
			"x-ga4-sid": ga4sid,
			authorization: `bearer ${token}`,
		},
	})
		.then(() => {
			localStorage.cid = cid;
			localStorage.ga4cid = ga4cid;
			localStorage.ga4sid = ga4sid;
		})
		.catch((err) => {
			console.error("[ANALYTICS] [updateCookies] Error updating cookies", err);
		});
}

/**
 * Safely reads the affiliate source from sessionStorage (private mode can throw).
 * @returns {string}
 */
function getAffiliateSource() {
	try {
		return sessionStorage.getItem("affiliate") || "n/a";
	} catch (e) {
		return "n/a";
	}
}

/**
 * Adds logged-in user fields and the hashed msisdn. Only called for
 * Login/Registration; other events get the universal decoration in trackEvent.
 * @param {Object} payload - The original event payload
 * @returns {Promise<Object>} - The decorated payload
 */
async function decorateDatalayerPayload(payload) {
	const user = auth.getUser();
	let decorated = Object.assign({}, payload);

	if (user) {
		Object.assign(decorated, {
			userId: user.id,
			profile_id: user.id,
			mobileNumber: user.mobile,
			mobile: user.mobile,
			userBalance: user.balance,
			bonusBalance: user.bonus,
			pointsBalance: user.points,
			user: {
				mobile: user.mobile,
				profile_id: user.id,
				id: user.id,
				user_id: user.id,
				wallet_balance: user.balance,
				points_balance: user.points,
				bonus_balance: user.bonus,
			},
		});
	}

	if (decorated.user && decorated.user.mobile) {
		try {
			const formatted = formatMobileNumber(decorated.user.mobile);
			if (formatted) {
				decorated.user.hashed_msisdn = await hashString(formatted);
			}
		} catch (e) {
			console.warn("[ANALYTICS] msisdn hashing failed", e);
		}
	}

	return decorated;
}

/**
 * Tracks an event across GA4, and conditionally Adjust and Firebase.
 * @param {Object} categoryObject - The category configuration object
 * @param {string} eventKey - The specific event key
 * @param {Object} data - The event data
 */
async function trackEvent(categoryObject, eventKey, data) {
	if (!categoryObject || !categoryObject[eventKey]) {
		console.warn(`[ANALYTICS] Invalid event configuration for: ${eventKey}`);
		return;
	}

	const eventInfo = categoryObject[eventKey];
	// Universal decoration is applied to every event so GTM variables resolve
	// to real values instead of "undefined".
	const payload = Object.assign({}, data, {
		event: eventInfo.event,
		eventCategory: eventInfo.eventCategory,
		src: getEventSource(),
		event_id: generateEventId(),
		app: "MOBILE_WEB",
		country: COUNTRY_CODE,
		languageCode: LANGUAGE_CODE,
		GeoLocation: GEOLOCATION,
		environment: process.env.NODE_ENV,
		utm_source: getAffiliateSource(),
	});
	if (payload.currency === undefined) payload.currency = window.CURRENCY;

	try {
		const finalPayload = ["Login", "Registration"].includes(eventInfo.eventCategory)
			? await decorateDatalayerPayload(payload)
			: payload;

		dataLayer.push(finalPayload);

		console.log(`[ANALYTICS] Event tracked: ${eventInfo.event}`, finalPayload);
	} catch (e) {
		console.warn(`[ANALYTICS] Error tracking event: ${eventInfo.event}`, e);
	}
}

/**
 * Creates analytics methods for a given category.
 * @param {Object} categories - The category configuration object
 * @returns {Object} - An object containing the created methods
 */
function createAnalyticsMethods(categories) {
	const methods = {};
	Object.keys(categories).forEach((eventName) => {
		methods[eventName] = (data) => {
			trackEvent(categories, eventName, data);
		};
	});
	return methods;
}

/**
 * The main analytics plugin object containing all tracking methods and utilities.
 */
const analyticsPlugin = {
	getUserInfo,
	registration: createAnalyticsMethods(registrationCategories),
	login: createAnalyticsMethods(loginCategories),
	deposit: createAnalyticsMethods(depositCategories),
	withdraw: createAnalyticsMethods(withdrawCategories),
	sportsbookBet: createAnalyticsMethods(sportsbookBetCategories),
	virtualsBet: createAnalyticsMethods(virtualsBetCategories),
	shikishaBet: createAnalyticsMethods(shikishaBetCategories),
	jackpotsBet: createAnalyticsMethods(jackpotsBetCategories),
	betikaFastaBet: createAnalyticsMethods(betikaFastaCasinoCategories),
	betSlip: createAnalyticsMethods(betSlipCategories),
	casino: createAnalyticsMethods(casinoCategories),
	iframe: createAnalyticsMethods(iframeCategories),
	general: createAnalyticsMethods(generalCategories),
	experiment: createAnalyticsMethods(experimentCategories),
};

export default analyticsPlugin;
